/**
 * Hook for fetching and managing a single job's detail data.
 * Polls for updates and exposes a refresh function.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchJob } from '../api/jobs';

const POLL_INTERVAL = 60_000; // 60 seconds

export default function useJob(jobId) {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    if (!jobId) return;
    try {
      const data = await fetchJob(jobId);
      setJob(data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load job');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  // Initial load
  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Polling
  useEffect(() => {
    intervalRef.current = setInterval(load, POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [load]);

  const refresh = useCallback(() => {
    load();
  }, [load]);

  return { job, loading, error, refresh, setJob };
}
